import { memo } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronUp } from 'lucide-react'
import { cn } from '@/lib/utils'
import { formatTimestamp } from '@/shared/lib/time'
import { Skeleton } from '@/shared/ui/Skeleton'
import type { PostCommentDto } from '../model/comments'
import { usePostComments } from '../state/usePostComments'

export function PostCommentsContainer({
  postId,
  onCollapse,
}: {
  postId: string
  onCollapse: () => void
}) {
  const data = usePostComments(postId)
  return <PostComments {...data} onCollapse={onCollapse} />
}

type Props = {
  comments: PostCommentDto[]
  total: number
  isLoading: boolean
  isFetchingNextPage: boolean
  hasNextPage: boolean
  fetchNextPage: () => void
  onCollapse: () => void
}

const PostComments = memo(function PostComments(props: Props) {
  const { t } = useTranslation()

  if (props.isLoading) {
    return (
      <div className="mt-3 border-t border-border pt-3">
        <CommentsSkeleton count={3} />
      </div>
    )
  }

  if (props.comments.length === 0) {
    return (
      <div className="mt-3 border-t border-border pt-3 text-center text-xs text-muted-foreground">
        {t('comments.empty', 'No comments yet')}
      </div>
    )
  }

  return (
    <div className="mt-3 border-t border-border pt-3">
      <div className="mb-1 flex items-center justify-between">
        <span className="text-xs font-medium text-muted-foreground">
          {t('comments.title', 'Comments')} ({props.total})
        </span>
        <button
          type="button"
          onClick={props.onCollapse}
          className="inline-flex items-center gap-0.5 text-xs text-muted-foreground transition-colors hover:text-foreground"
        >
          {t('comments.collapse', 'Collapse')}
          <ChevronUp size={12} />
        </button>
      </div>

      <div className="divide-y divide-border/50">
        {props.comments.map((c) => (
          <CommentItem key={c.id} comment={c} />
        ))}
      </div>

      {props.hasNextPage && (
        <div className="mt-2 grid place-items-center">
          <button
            type="button"
            onClick={props.fetchNextPage}
            disabled={props.isFetchingNextPage}
            className={cn(
              'rounded-lg px-3 py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground',
              props.isFetchingNextPage && 'opacity-50',
            )}
          >
            {props.isFetchingNextPage
              ? t('common.loadingDots', 'Loading...')
              : t('comments.loadMore', 'Load more comments')}
          </button>
        </div>
      )}

      {props.isFetchingNextPage && <CommentsSkeleton count={2} />}
    </div>
  )
})

const CommentItem = memo(function CommentItem({ comment }: { comment: PostCommentDto }) {
  return (
    <div className="flex gap-2.5 py-2.5">
      <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-secondary text-xs font-medium text-foreground">
        {comment.author.name.charAt(0).toUpperCase()}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5 text-xs">
          <span className="truncate font-semibold text-foreground">
            {comment.author.name}
          </span>
          <span className="text-muted-foreground">&middot;</span>
          <time dateTime={comment.timestamp} className="shrink-0 text-muted-foreground">
            {formatTimestamp(comment.timestamp)}
          </time>
        </div>
        <p className="mt-0.5 whitespace-pre-wrap break-words text-sm leading-relaxed text-foreground">
          {comment.content}
        </p>
      </div>
    </div>
  )
})

function CommentsSkeleton({ count }: { count: number }) {
  return (
    <div className="flex flex-col gap-3">
      {Array.from({ length: count }, (_, i) => (
        <div key={i} className="flex gap-2.5 py-2.5">
          <Skeleton className="h-7 w-7 shrink-0 rounded-full" />
          <div className="flex-1 space-y-1.5">
            <Skeleton className="h-3 w-24" />
            <Skeleton className="h-4 w-full" />
          </div>
        </div>
      ))}
    </div>
  )
}
